import React, { useState, useRef, useEffect } from 'react';

const ChatPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const messagesEndRef = useRef(null);
  const chatGenerationRef = useRef(0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const clearVisibleChat = () => {
      chatGenerationRef.current += 1;
      setMessages([]);
      setInputValue('');
      setIsLoading(false);
    };
    window.addEventListener('promptcrm-chat-cleared', clearVisibleChat);
    return () => window.removeEventListener('promptcrm-chat-cleared', clearVisibleChat);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    fetch('/api/chat/history', {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    })
      .then(response => response.ok ? response.json() : Promise.reject(new Error('History request failed.')))
      .then(data => setMessages((data.messages || []).map(message => ({
        type: message.role === 'user' ? 'user' : 'bot',
        content: message.content
      }))))
      .catch(() => {});
  }, [isOpen]);

  const sendMessage = async () => {
    if (!inputValue.trim()) return;

    const generation = chatGenerationRef.current;
    const userMessage = { type: 'user', content: inputValue };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/chat/chat', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: inputValue }),
      });

      const data = await response.json();

      let botMessage = {
        type: 'bot',
        content: data.message,
        messageType: data.type
      };

      if (data.note) {
        botMessage.note = data.note;
      }

      if (data.data) {
        botMessage.data = data.data;
      }

      if (generation === chatGenerationRef.current) {
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      if (generation === chatGenerationRef.current) {
        setMessages(prev => [...prev, {
          type: 'bot',
          content: 'Sorry, there was an error processing your message.',
          messageType: 'error'
        }]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const startNewChat = async () => {
    setIsClearing(true);
    try {
      const response = await fetch('/api/chat/history', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('The chat history could not be cleared.');
      window.dispatchEvent(new Event('promptcrm-chat-cleared'));
    } catch (error) {
      setMessages(prev => [...prev, {
        type: 'bot',
        content: 'The chat history could not be cleared.',
        messageType: 'error'
      }]);
    } finally {
      setIsClearing(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getMessageStyle = (messageType) => {
    switch (messageType) {
      case 'hint':
        return 'bg-yellow-50 border border-yellow-200 text-yellow-800';
      case 'success':
        return 'bg-green-50 border border-green-200 text-green-800';
      case 'error':
        return 'bg-red-50 border border-red-200 text-red-800';
      case 'chatgpt':
        return 'bg-blue-50 border border-blue-200 text-blue-800';
      case 'result':
        return 'bg-purple-50 border border-purple-200 text-purple-800';
      default:
        return 'bg-gray-50 border border-gray-200 text-gray-800';
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Chat Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-4 shadow-lg transition-all duration-200 flex items-center justify-center"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className="bg-white rounded-lg shadow-xl border w-[calc(100vw-2rem)] sm:w-[28rem] h-[calc(100vh-2rem)] flex flex-col">
          {/* Header */}
          <div className="bg-blue-500 text-white p-4 rounded-t-lg flex justify-between items-center">
            <h3 className="font-semibold">Chat Assistant</h3>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={startNewChat}
                disabled={isLoading || isClearing}
                className="text-sm text-white hover:text-blue-100 disabled:opacity-50 transition-colors"
              >
                {isClearing ? 'Clearing...' : 'New chat'}
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-white hover:text-gray-200 transition-colors"
                aria-label="Close chat"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-gray-500 text-center text-sm">
                Start a conversation! You can add contacts, find contacts, or ask questions.
              </div>
            )}

            {messages.map((message, index) => (
              <div key={index} className={`${message.type === 'user' ? 'text-right' : 'text-left'}`}>
                <div className={`inline-block max-w-xs px-3 py-2 rounded-lg ${message.type === 'user'
                    ? 'bg-blue-500 text-white'
                    : getMessageStyle(message.messageType)
                  }`}>
                  <div className="text-sm">{message.content}</div>
                  {message.note && (
                    <div className="text-xs mt-1 opacity-75">{message.note}</div>
                  )}
                  {message.data && (
                    <div className="text-xs mt-1 bg-black bg-opacity-10 p-2 rounded">
                      {JSON.stringify(message.data, null, 2)}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="text-left">
                <div className="inline-block bg-gray-200 px-3 py-2 rounded-lg">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message..."
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !inputValue.trim()}
                className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPanel;
